import { AGENT_TURN_MAX_SECONDS } from '@tunasong/schemas'
import type { Agent, AgentRuntimeStatus, Persisted } from '@tunasong/schemas'

/**
 * Client-side TTL for a `thinking` broadcast that never resolved (e.g. the
 * Lambda died mid-turn). Derived from the server's own turn ceiling plus WS
 * round-trip margin — the client never declares a turn dead while the server
 * is still allowed to run it (the old hardcoded 90s did exactly that against
 * the 180s Lambda timeout).
 */
export const RUNTIME_STATUS_STALE_MS = (AGENT_TURN_MAX_SECONDS + 30) * 1000

interface ClockDeps {
  now?: () => number
}

/**
 * Derived live runtime status for an agent. Pure function — no React
 * state, no timers, no re-render side-effects. Renders are recomputed
 * any time the agent entity updates in the graph cache; the staleness
 * fallback applies as a render-time derivation.
 *
 * If a stuck `thinking` is older than RUNTIME_STATUS_STALE_MS, this
 * collapses to `idle`. The visual treatment for the stale window
 * (e.g. a softer "took too long" badge) is rendered upstream when the
 * panel is open — out of scope here.
 */
export const deriveAgentRuntimeStatus = (
  agent: Pick<Persisted<Agent>, 'runtimeStatus' | 'runtimeStatusAt'>,
  deps: ClockDeps = {}
): AgentRuntimeStatus => {
  const status = agent.runtimeStatus
  if (!status || status === 'idle') {
    return 'idle'
  }
  if (status === 'thinking' && agent.runtimeStatusAt) {
    const now = deps.now ? deps.now() : Date.now()
    const ageMs = now - new Date(agent.runtimeStatusAt).getTime()
    if (Number.isFinite(ageMs) && ageMs > RUNTIME_STATUS_STALE_MS) {
      return 'idle'
    }
  }
  return status
}
